import React from 'react';
import config from '../../config';

const LocalBusinessSchema = ({ 
  businessName,
  description,
  address,
  city,
  state,
  zipCode,
  phone,
  website,
  image = null,
  rating = null,
  reviewCount = null,
  reviews = [], // Array of actual review objects
  priceRange = '$$',
  latitude = null,
  longitude = null,
  services = [], // Services offered by the business (specific services provided)
  serviceAreas = null, // Geographic areas served (cities, neighborhoods, etc.)
  service_areas = null, // Alternative property name for service areas
  subscriptionPlan = 'BASIC', // Added to check if premium/verified
  isSponsored = false // Added to check if sponsored
}) => {
  // Use serviceAreas or service_areas, ensuring we always have an array
  // First make sure we initialize values to prevent undefined errors
  const safeServiceAreas = serviceAreas || [];
  const safeServiceAreas2 = service_areas || [];
  // Now safely check their length properties
  const areas = safeServiceAreas.length ? safeServiceAreas : 
                (safeServiceAreas2.length ? safeServiceAreas2 : []);
  
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": businessName,
    "description": description,
    "telephone": phone,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": address,
      "addressLocality": city,
      "addressRegion": state,
      "postalCode": zipCode,
      "addressCountry": "US"
    },
    "url": website || `${config.siteUrl}/contractor/${encodeURIComponent((businessName || '').toLowerCase().replace(/\s+/g, '-') )}`,
    "priceRange": priceRange,
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
      ],
      "opens": "00:00",
      "closes": "23:59"
    }
  };

  // Add image if provided AND contractor is premium/verified/sponsored
  if (image && (subscriptionPlan === 'PREMIUM' || subscriptionPlan === 'VERIFIED' || isSponsored)) {
    schemaData.image = image;
  }

  // Add geo coordinates if provided
  if (latitude && longitude) {
    schemaData.geo = {
      "@type": "GeoCoordinates",
      "latitude": latitude,
      "longitude": longitude
    };
  }

  // Add services if provided - these are the specific services offered
  if (services && services.length > 0) {
    schemaData.hasOfferCatalog = {
      "@type": "OfferCatalog",
      "name": "Water Damage Services",
      "itemListElement": services.map((service, index) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service
        }
      }))
    };
  }
  
  // Add service areas if provided - these are geographic areas served
  if (areas && areas.length > 0) {
    schemaData.areaServed = areas.map(area => ({
      "@type": "City",
      "name": area
    }));
  }

  // Add aggregate rating if provided
  if (rating && reviewCount) {
    schemaData.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": rating,
      "reviewCount": reviewCount
    };
    
    // Add actual reviews if available
    if (reviews && reviews.length > 0) {
      // If we have actual review objects, use them for schema
      schemaData.review = reviews.slice(0, 5).map(review => ({
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": review.rating
        },
        "author": {
          "@type": "Person",
          "name": review.reviewer_name || "Customer"
        },
        "datePublished": review.review_date || new Date().toISOString().split('T')[0],
        "reviewBody": review.review_text || `Rated ${review.rating} out of 5 stars`
      }));
    } else if (reviewCount > 0) {
      // Fallback to generic review if we have count but no specific reviews
      schemaData.review = {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": rating
        },
        "author": {
          "@type": "Person",
          "name": "Customer Review"
        },
        "reviewBody": `${businessName} has ${reviewCount} customer reviews with an average rating of ${rating} out of 5 stars.`
      };
    }
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
    />
  );
};

export default LocalBusinessSchema;